/* Decotimber · Lote 4. Family collection: Shopify filters and sorting through the Section Rendering API,
   «Cargar más» with the same filters, and the mobile filter panel (scroll lock, focus trap, Escape).
   Every count, value and URL comes from the section Shopify renders; nothing is filtered in the browser. */
(() => {
  'use strict';
  if (window.DecotimberCollection) return;
  const mobile = window.matchMedia('(max-width: 989px)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class Collection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.controller = null;
      root.addEventListener('change', event => this.onChange(event));
      root.addEventListener('click', event => this.onClick(event));
      root.addEventListener('keydown', event => this.onKeydown(event));
      window.addEventListener('popstate', () => this.render(window.location.href, { push: false }));
      mobile.addEventListener('change', () => { if (!mobile.matches) this.close(false); });
    }

    get panel() { return this.root.querySelector('[data-dt-panel]'); }
    get isOpen() { return this.root.classList.contains('is-panel-open'); }

    // Current URL params, with the visible filter groups replaced by the form state. Filters of the app that the
    // section does not show (and sort_by) are kept, so sorting or loading more never drops an applied filter.
    urlFromForm() {
      const form = this.root.querySelector('[data-dt-filter-form]');
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      const names = new Set([...form.elements].map(el => el.name).filter(name => name && name !== 'sort_by'));
      names.forEach(name => params.delete(name));
      new FormData(form).forEach((value, name) => { if (name !== 'sort_by' && String(value).trim() !== '') params.append(name, value); });
      params.delete('page');
      params.delete('section_id');
      url.search = params.toString();
      return url.href;
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-dt-sort]')) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', target.value);
        url.searchParams.delete('page');
        this.render(url.href, { focus: target.id });
      } else if (target.closest('[data-dt-filter-form]') && target.type !== 'number') {
        this.render(this.urlFromForm(), { focus: target.id });
      } else if (target.closest('[data-dt-filter-form]')) {
        clearTimeout(this.rangeTimer);
        this.rangeTimer = setTimeout(() => this.render(this.urlFromForm(), { focus: target.id }), 500);
      }
    }

    onClick(event) {
      const link = event.target.closest('a[data-dt-link]');
      const more = event.target.closest('a[data-dt-load-more]');
      if (link) { event.preventDefault(); this.render(link.href, { focus: this.isOpen ? 'close' : 'results' }); }
      else if (more) { event.preventDefault(); this.loadMore(more); }
      else if (event.target.closest('[data-dt-open]')) this.open();
      else if (event.target.closest('[data-dt-close]') || event.target.closest('[data-dt-backdrop]')) this.close(true);
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
      if (event.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    open() {
      if (!mobile.matches) return;
      const panel = this.panel;
      this.root.classList.add('is-panel-open');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      this.root.querySelector('[data-dt-backdrop]').hidden = false;
      this.root.querySelector('[data-dt-open]')?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('dt-scroll-lock');
      panel.querySelector('[data-dt-close]')?.focus();
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      const panel = this.panel;
      this.root.classList.remove('is-panel-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      this.root.querySelector('[data-dt-backdrop]').hidden = true;
      const opener = this.root.querySelector('[data-dt-open]');
      opener?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('dt-scroll-lock');
      if (returnFocus) opener?.focus();
    }

    async fetchSection(url, signal) {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      const response = await fetch(target.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-dt-collection]');
      if (!fresh) throw new Error('Missing collection section');
      return fresh;
    }

    // Replaces the section with Shopify's render for the new URL; keeps the mobile panel open, its scroll and focus.
    async render(url, { push = true, focus = '' } = {}) {
      this.controller?.abort();
      const controller = (this.controller = new AbortController());
      const wasOpen = this.isOpen;
      const scroll = this.panel.querySelector('.dt-filters__body')?.scrollTop || 0;
      this.root.setAttribute('aria-busy', 'true');
      try {
        const fresh = await this.fetchSection(url, controller.signal);
        if (controller.signal.aborted) return;
        this.root.querySelector('.dt-cat__inner').replaceWith(fresh.querySelector('.dt-cat__inner'));
        if (push) window.history.pushState({}, '', url);
        if (wasOpen) {
          this.root.classList.add('is-panel-open');
          this.panel.setAttribute('role', 'dialog');
          this.panel.setAttribute('aria-modal', 'true');
          this.root.querySelector('[data-dt-backdrop]').hidden = false;
          this.root.querySelector('[data-dt-open]')?.setAttribute('aria-expanded', 'true');
          const body = this.panel.querySelector('.dt-filters__body');
          if (body) body.scrollTop = scroll;
        }
        this.restoreFocus(focus, wasOpen);
        this.root.dispatchEvent(new CustomEvent('decotimber:collection:updated', { bubbles: true }));
      } catch (error) {
        if (error.name !== 'AbortError') { console.error('[Decotimber collection]', error); window.location.href = url; }
      } finally {
        if (this.controller === controller) this.root.removeAttribute('aria-busy');
      }
    }

    restoreFocus(focus, wasOpen) {
      let el = null;
      if (focus === 'close') el = this.panel.querySelector('[data-dt-close]');
      else if (focus === 'results') el = this.root.querySelector('.dt-col__count');
      else if (focus) el = document.getElementById(focus);
      if (!el && wasOpen) el = this.panel.querySelector('[data-dt-close]');
      if (el) { if (focus === 'results') el.setAttribute('tabindex', '-1'); el.focus({ preventScroll: true }); }
    }

    // Appends Shopify's next page (same filters and order, from paginate.next.url) before the quote card.
    async loadMore(link) {
      if (link.getAttribute('aria-disabled') === 'true') return;
      link.setAttribute('aria-disabled', 'true');
      try {
        const fresh = await this.fetchSection(link.href);
        const grid = this.root.querySelector('[data-dt-grid]');
        const promo = grid.querySelector('[data-dt-promo]');
        const items = [...fresh.querySelectorAll('[data-dt-grid] > [data-dt-item]')];
        items.forEach(item => grid.insertBefore(item, promo));
        this.root.querySelector('[data-dt-more]').replaceWith(fresh.querySelector('[data-dt-more]'));
        items[0]?.querySelector('.dt-pcard__title a')?.focus();
        this.root.dispatchEvent(new CustomEvent('decotimber:collection:updated', { bubbles: true }));
      } catch (error) {
        console.error('[Decotimber collection]', error);
        window.location.href = link.href;
      }
    }
  }

  window.DecotimberCollection = Collection;
  const start = () => document.querySelectorAll('[data-dt-collection]').forEach(root => { if (!root.dtCollection) root.dtCollection = new Collection(root); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
